/* eslint-disable react-hooks/rules-of-hooks */
import { useMutation, useQuery } from "@tanstack/react-query";
import { MusicIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAudio } from "../hooks/useAudio";
import { useAuth } from "../hooks/useAuth";
import { useMusicControl } from "../hooks/useMusicControl";
import { useScreenSocketInfo } from "../hooks/useScreenSocket";
import { cn } from "../lib/utils";
import { LoadingSpinner } from "./LoadingConitainer";
import { MusicControlIndicator } from "./MusicControlIndicator";
import { RequestControlDialog } from "./RequestControlDialog";
import { ScrollArea } from "./scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { ButtonCard, CardHeader, CardTitle } from "./ui/card";

interface MusicSelectionDialogProps {
  onClose: () => void;
}

export type MusicChannel = {
  id: number
  name: string
  description: string
}
type MusicApiFetchResponse<TSuccess, TFailure = { message: string }> = ({
  status: "success",
} & TSuccess) | ({
  status: "error" | "failed"
} & TFailure)
export type ChangePlaylistResponse = MusicApiFetchResponse<void>
export type FetchPlaylistResponse = MusicApiFetchResponse<{
  playlists: MusicChannel[]
}>

export const useMusicQuery = {
  $metadata: {
    headers() {
      const headers = new Headers();
      headers.append("Authorization", `Bearer ${import.meta.env.VITE_MUSIC_API_KEY}`);
      return headers
    },
  },
  changePlaylist() {
    const headers = this.$metadata.headers()
    return useMutation({
      async mutationFn({ zone_id, playlist_id }: { zone_id: number, playlist_id: number }) {
        const apiUrl = `${import.meta.env.VITE_MUSIC_API_URL}/api/partner/location/zone/change_playlist`

        const resp = await fetch(apiUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({
            zone_id,
            playlist_id,
          })
        })

        const json = await resp.json() as ChangePlaylistResponse

        if (json.status === "success") {
          return resp
        }

        throw new Error(json.message)
      }
    })
  },
  playlists() {
    return useQuery({
      queryKey: ["music-playlists"],
      staleTime: Infinity,
      queryFn: async () => {
        const apiUrl = `${import.meta.env.VITE_MUSIC_API_URL}/api/partner/playlists`

        const resp = await fetch(apiUrl, {
          method: "POST",
          headers: this.$metadata.headers(),
        })

        const musicInfo = await resp.json() as FetchPlaylistResponse
        if (musicInfo.status === "success") {
          return musicInfo.playlists
        }
        throw new Error(musicInfo.message)
      },
    })
  },
}

export default function MusicSelectionDialog(props: MusicSelectionDialogProps) {
  const [showRequestControl, setShowRequestControl] = useState(false);
  const [showUnsyncDialog, setShowUnsyncDialog] = useState(false);
  const { screenInfo: screen, updateScreenInfo } = useAuth();
  const { controlInfo } = useMusicControl(screen?.id ?? '', screen?.location_id, screen?.group_id);
  const hasControl = !screen?.group_id || controlInfo?.has_control || controlInfo?.is_owner
  const { socket } = useScreenSocketInfo();
  const mutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/api/musicControl`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screen_id: screen?.id,
          location_id: screen?.location_id,
          action: 'delete'
        })
      });
      if (!response.ok) {
        throw new Error('Failed to unsync music');
      }
    },
    onSuccess: () => {
      updateScreenInfo({
        id: screen!.id,
        group_id: null,
        music_playing: false,
        last_music_id: null
      });
      socket?.emit("broadcastMessage", {
        roomId: screen!.id,
        messageContent: "musicSyncDisabled",
        messageData: { from: "dashboard" }
      });
      setShowUnsyncDialog(false);
      props.onClose();
    },
    onError: () => {
      // alert(`Failed to unsync music: ${error.message}`);
    }
  });

  const handleRequestSent = () => {
    setShowRequestControl(false);
  };

  const [focusedUnsyncDialogIndex, setFocusedUnsyncDialogIndex] = useState(0); // 0 for Cancel, 1 for Unsync
  const cancelUnsyncRef = useRef<HTMLButtonElement>(null);
  const unsyncRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showUnsyncDialog) {
      // Focus the Cancel button when the dialog opens
      cancelUnsyncRef.current?.focus();
    }
  }, [showUnsyncDialog]);

  const handleUnsyncDialogKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowUnsyncDialog(false);
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setFocusedUnsyncDialogIndex((prevIndex) => {
        const newIndex = prevIndex === 0 ? 1 : 0;
        if (newIndex === 0) {
          cancelUnsyncRef.current?.focus();
        } else {
          unsyncRef.current?.focus();
        }
        return newIndex;
      });
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (focusedUnsyncDialogIndex === 0) {
        setShowUnsyncDialog(false);
      } else {
        mutation.mutate();
      }
    }
  }, [focusedUnsyncDialogIndex, mutation, setShowUnsyncDialog]);

  useEffect(() => {
    if (showUnsyncDialog) {
      window.addEventListener('keydown', handleUnsyncDialogKeyDown);
    } else {
      window.removeEventListener('keydown', handleUnsyncDialogKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleUnsyncDialogKeyDown);
    };
  }, [showUnsyncDialog, handleUnsyncDialogKeyDown]);

  return (
    <div
      id="music-popup"
      className="absolute flex h-screen w-screen items-center justify-center bg-black bg-opacity-50"
    >
      <div className="flex flex-col items-center justify-center gap-4 rounded-2xl bg-white px-12 py-12 text-black md:px-[5vw] md:py-[5vh]">
        {showRequestControl ? (
          <RequestControlDialog
            onClose={() => setShowRequestControl(false)}
            onRequestSent={handleRequestSent}
          />
        ) : (
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Select Music</h2>
              <div className="flex items-center gap-4">
                {screen?.group_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    id="unsync-music-btn"
                    onClick={() => setShowUnsyncDialog(true)}
                  >
                    Unsync Music
                  </Button>
                )}
                <MusicControlIndicator
                  screenId={screen?.id ?? ''}
                  locationId={screen?.location_id ?? ''}
                  groupId={screen?.group_id ?? undefined}
                  className="ml-4"
                />
              </div>
            </div>
            {hasControl ? (
              <MusicGridFetcher {...props} />
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">You need permission to control music in this location</p>
                <ButtonCard onClick={() => setShowRequestControl(true)}>
                  Request Control
                </ButtonCard>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={showUnsyncDialog} onOpenChange={setShowUnsyncDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsync Music</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unsync music from this location? This will remove all music
              control settings for this screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-4">
            <AlertDialogCancel ref={cancelUnsyncRef} className="focus:scale-150">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="focus:scale-150"
              ref={unsyncRef}
              onClick={() => {
                mutation.mutate();
              }}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Unsyncing...' : 'Unsync'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MusicGridFetcher(props: MusicSelectionDialogProps) {
  const { isLoading, data = [] } = useMusicQuery.playlists();

  if (isLoading) return <LoadingSpinner />

  return <MusicGridPresenter musicChannels={data} {...props} />
}

function MusicGridPresenter({ musicChannels, onClose }: { musicChannels: MusicChannel[] } & MusicSelectionDialogProps) {
  const { socket } = useScreenSocketInfo()
  const { screenInfo: screen, updateScreenInfo } = useAuth()
  const { onTrackChange } = useAudio()

  const lastMusicId = screen?.last_music_id

  const playingChannel = !lastMusicId ? undefined : musicChannels.find(c => c.id === lastMusicId)

  const changePlaylistMutation = useMusicQuery.changePlaylist()
  const [focusedMusicIndex, setFocusedMusicIndex] = useState(0)

  function pauseMusic() {
    updateScreenInfo({
      id: screen!.id,
      music_playing: false,
    })
  }

  function emitMusicEvent(event: string, playlist_id: number) {
    socket?.emit("broadcastMessage", {
      roomId: screen!.id,
      messageContent: event,
      messageData: {
        from: "player",
        playlist_id,
      },
    })
  }

  async function playMusic(zone_id: number, playlist_id: number) {
    updateScreenInfo({
      id: screen!.id,
      last_music_id: playlist_id,
      music_playing: true,
    })
    await changePlaylistMutation.mutateAsync({ zone_id, playlist_id })
    emitMusicEvent("musicChanged", playlist_id)
    emitMusicEvent("musicResumeRequested", playlist_id)
    onTrackChange()
    onClose()
  }

  const musicChannelIds = useMemo(
    () => musicChannels.map(m => m.id.toString()),
    [musicChannels]
  )

  function focusIndex(i: number) {
    if (i === -1) return document.getElementById('unsync-music-btn')?.focus()
    document.getElementById(musicChannelIds[i])?.focus()
  }

  function getGridColumns() {
    const gridElement = document.querySelector('.grid');
    if (!gridElement) return 1;
    const style = window.getComputedStyle(gridElement);
    return style.gridTemplateColumns.split(' ').length;
  }

  function stopPropagationAndChangeFocusBy(e: KeyboardEvent, direction: 'left' | 'right' | 'up' | 'down') {
    e.stopPropagation();

    setFocusedMusicIndex((currentIndex) => {
      let newIndex = currentIndex;
      const columns = getGridColumns();
      const totalItems = musicChannels.length;

      if (currentIndex === -1) {
        // If currently unfocused, any arrow key moves to the first item (index 0)
        newIndex = 0;
      } else {
        switch (direction) {
          case 'right':
            newIndex = (currentIndex === totalItems - 1) ? -1 : currentIndex + 1;
            break;
          case 'left':
            newIndex = (currentIndex === 0) ? -1 : currentIndex - 1;
            break;
          case 'up': {
            const targetIndex = currentIndex - columns;
            newIndex = (targetIndex < 0) ? -1 : targetIndex;
            break;
          }
          case 'down': {
            const targetIndex = currentIndex + columns;
            newIndex = (targetIndex >= totalItems) ? -1 : targetIndex;
            break;
          }
        }
      }

      focusIndex(newIndex);
      return newIndex;
    });
  }

  useEffect(() => {
    focusIndex(focusedMusicIndex);
    function listener(e: KeyboardEvent) {
      switch (e.code) {
        case "ArrowRight":
          stopPropagationAndChangeFocusBy(e, 'right');
          break;
        case "ArrowLeft":
          stopPropagationAndChangeFocusBy(e, 'left');
          break;
        case "ArrowUp":
          stopPropagationAndChangeFocusBy(e, 'up');
          break;
        case "ArrowDown":
          stopPropagationAndChangeFocusBy(e, 'down');
          break;
        case "Escape":
          e.stopPropagation();
          onClose();
      }
    }

    document.addEventListener("keyup", listener);
    return () => document.removeEventListener("keyup", listener);
  }, [focusedMusicIndex, musicChannelIds]);

  return (
    <ScrollArea id="rootMusicPickerElement" className="container h-svh">
      <div className="container mx-auto p-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {musicChannels.map((channel, i) => {
            const isPlaying = screen?.music_playing && playingChannel?.id === channel.id
            return <ButtonCard
              key={musicChannelIds[i]}
              id={musicChannelIds[i]}
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                if (isPlaying) {
                  pauseMusic()
                } else if (screen?.zone_id) {
                  playMusic(screen.zone_id, channel.id)
                }
              }}
              className={cn("h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1", {
                "border-accent bg-green-200": isPlaying,
                "border-ring": channel.id === screen?.last_music_id,
                "focus:border-ring focus:scale-110": true,
              })}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MusicIcon className="h-5 w-5" />
                  <h1 className="w-full">
                    {channel.name}
                  </h1>
                </CardTitle>
              </CardHeader>
            </ButtonCard>
          })}
        </div>
      </div>
    </ScrollArea>
  )
}